import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useDecodedToken from "../../utils/useDecodedToken";

const GROUPS = [
  {
    key: "tournaments",
    label: "Tournaments",
    icon: "fas fa-trophy",
    href: "/tournaments",
    items: [
      { label: "Active Tournaments", href: "/tournaments/active" },
      { label: "Past Tournaments", href: "/past-tournaments" },
      { label: "Manage Tournaments", href: "/manage-tournaments" },
    ],
  },
  {
    // `key` and `href` are deliberately left as "ranking" / "/solo-duo-ranking": they are
    // route and state identifiers, and changing them would break existing links.
    key: "ranking",
    label: "Solo/Duo Ranking",
    icon: "fas fa-medal",
    href: "/solo-duo-ranking",
    items: [
      { label: "Standings", href: "/solo-duo-ranking/standings" },
      { label: "Manage Registration", href: "/solo-duo-ranking/manage-registration" },
      { label: "Invitations", href: "/solo-duo-ranking/invitations" },
      { label: "Rules", href: "/solo-duo-ranking/rules" },
    ],
  },
  {
    key: "raids",
    label: "Raids",
    icon: "fas fa-shield-halved",
    href: "/raid-signups",
    items: [
      { label: "Raid Sign-Up", href: "/raid-signups" },
      { label: "Raid Statistics", href: "/raid-statistics" },
      { label: "Audit Log", href: "/raid-signups/audit-log", adminOnly: true },
    ],
  },
];

const isActive = (pathname, href) =>
  pathname === href || pathname.startsWith(`${href}/`);

const isGroupActive = (pathname, group) =>
  isActive(pathname, group.href) ||
  group.items.some((item) => isActive(pathname, item.href));

const Sidebar = ({ open, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { decodedJwt } = useDecodedToken();
  const isAdmin = decodedJwt?.role === "admin";

  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    GROUPS.forEach((group) => {
      initial[group.key] = isGroupActive(location.pathname, group);
    });
    return initial;
  });

  const toggleGroup = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNavigate = (href) => {
    navigate(href);
    onClose?.();
  };

  const handleGroupParentClick = (group) => {
    setExpanded((prev) => ({ ...prev, [group.key]: true }));
    handleNavigate(group.href);
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed lg:static top-16 lg:top-0 left-0 h-[calc(100%-4rem)] lg:h-full w-64 flex-shrink-0 bg-gray-900/95 border-r border-gray-700 overflow-y-auto z-50 transform transition-transform duration-300 lg:transform-none ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <nav className="p-3 space-y-1">
          <SidebarLink
            href="/"
            icon="fas fa-house"
            label="Home"
            active={location.pathname === "/"}
            onClick={() => handleNavigate("/")}
          />

          {GROUPS.map((group) => (
            <div key={group.key}>
              <div
                className={`flex items-center rounded-lg text-sm font-medium transition-all duration-200 ${
                  isGroupActive(location.pathname, group)
                    ? "text-white bg-blue-500/20"
                    : "text-gray-300 hover:text-white hover:bg-gray-700/50"
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleGroupParentClick(group)}
                  className="flex items-center space-x-3 flex-1 px-3 py-2.5 text-left"
                >
                  <i className={`${group.icon} text-blue-400 w-4 text-center`}></i>
                  <span>{group.label}</span>
                </button>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  aria-label={`Toggle ${group.label}`}
                  className="px-3 py-2.5"
                >
                  <i
                    className={`fas fa-chevron-${
                      expanded[group.key] ? "up" : "down"
                    } text-xs transition-transform duration-200`}
                  ></i>
                </button>
              </div>

              {expanded[group.key] && (
                <div className="ml-6 mt-1 mb-2 space-y-1 border-l border-gray-700 pl-3">
                  {group.items
                    .filter((item) => !item.adminOnly || isAdmin)
                    .map((item) => (
                      <SidebarLink
                        key={item.href}
                        href={item.href}
                        label={item.label}
                        active={isActive(location.pathname, item.href)}
                        onClick={() => handleNavigate(item.href)}
                        compact
                      />
                    ))}
                </div>
              )}
            </div>
          ))}

          <SidebarLink
            href="/shop"
            icon="fas fa-store"
            label="Shop"
            active={isActive(location.pathname, "/shop") || location.pathname === "/cart"}
            onClick={() => handleNavigate("/shop")}
          />
        </nav>
      </aside>
    </>
  );
};

const SidebarLink = ({ href, icon, label, active, onClick, compact }) => (
  <a
    href={href}
    onClick={(e) => {
      e.preventDefault();
      onClick();
    }}
    className={`flex items-center space-x-3 rounded-lg text-sm font-medium transition-all duration-200 ${
      compact ? "px-3 py-1.5" : "px-3 py-2.5"
    } ${
      active
        ? "text-white bg-blue-500/20"
        : "text-gray-300 hover:text-white hover:bg-gray-700/50"
    }`}
  >
    {icon && <i className={`${icon} text-blue-400 w-4 text-center`}></i>}
    <span>{label}</span>
  </a>
);

export default Sidebar;
